/**
 * Ashra AI — Premium 2026 Landing Page (Phase J).
 *
 * THE AI REVENUE OPERATING SYSTEM.
 * Built to the design_guidelines.json blueprint:
 *   - Archetype 4 (Swiss, high-contrast) + premium purple→pink→orange gradient accents
 *   - 12 sections: hero, trust strip, AI team, dashboard showcase, features grid,
 *     how-it-works, pricing, audience grid, Growth Hub preview, FAQ, final CTA, footer
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, PlayCircle, Sparkles, Target, MessageSquare, RefreshCw, Brain,
  Inbox, Users, Workflow, ShoppingBag, CreditCard, BarChart3, Zap, Bot,
  Check, Star, Plus, Minus, Crown, ChevronRight, Mail,
} from "lucide-react";
import { SiteFooter } from "../components/marketing/SiteFooter";

const LOGO = "/ashra-logo.png";
const DASH_MOCKUP = "/marketing/dashboard-mockup.png";
const HERO_VISUAL = "/marketing/hero-visual.png";

const AI_TEAM = [
  { name: "Hunter AI", role: "Finds & qualifies opportunities", task: "Scanning new leads…", img: "/marketing/hunter-ai.png", accent: "from-violet-500 to-fuchsia-500" },
  { name: "Closer AI", role: "Converts prospects into customers", task: "Sending proposal…", img: "/marketing/closer-ai.png", accent: "from-fuchsia-500 to-rose-500" },
  { name: "Recovery AI", role: "Recovers lost revenue", task: "Following up on cart…", img: "/marketing/recovery-ai.png", accent: "from-rose-500 to-orange-500" },
  { name: "Strategist AI", role: "Provides growth recommendations", task: "Analyzing funnel…", img: "/marketing/strategist-ai.png", accent: "from-orange-500 to-amber-500" },
];

const TRUST_AUDIENCES = ["Creators", "Coaches", "Consultants", "Agencies", "SaaS Founders", "Digital Product Sellers"];

const FEATURES = [
  { icon: Inbox, title: "AI Inbox", outcome: "Never miss a lead across Instagram, WhatsApp, and email — your agents reply in seconds." },
  { icon: Users, title: "CRM & Leads", outcome: "Every conversation, score, and stage in one view. Hot prospects surface automatically." },
  { icon: Workflow, title: "Funnels & Pages", outcome: "Launch high-converting landing pages and booking flows without a designer." },
  { icon: ShoppingBag, title: "Storefront", outcome: "Sell digital products, courses, and memberships with one-click checkout." },
  { icon: CreditCard, title: "Payments & Billing", outcome: "Paddle-powered subscriptions, taxes, and invoices handled globally." },
  { icon: BarChart3, title: "Revenue Analytics", outcome: "See exactly which channel, agent, and offer is driving your growth." },
  { icon: Zap, title: "Marketing Automation", outcome: "Lifecycle campaigns that nurture, upsell, and reactivate on autopilot." },
  { icon: Bot, title: "AI Agents", outcome: "Four AI teammates that work 24/7 to capture, close, recover, and strategize." },
];

const PLANS = [
  {
    slug: "ignite", name: "IGNITE", price: 27, recommended: false,
    audience: "Creators · Coaches · Freelancers · Small Businesses",
    bullets: [
      "AI Inbox + 1 AI Revenue Agent",
      "CRM, Leads & Lead Intelligence",
      "Landing Pages, Funnels & Booking",
      "Smart Checkout & Storefront",
      "WhatsApp lead capture",
      "Basic analytics",
    ],
    limits: ["500 AI conversations / month", "1,000 contacts", "1 funnel", "1 user", "1 GB storage"],
  },
  {
    slug: "ascend", name: "ASCEND", price: 99, recommended: true,
    audience: "Agencies · Consultants · Growth Teams · Scaling Businesses",
    bullets: [
      "Everything in IGNITE plus:",
      "AI Strategist (revenue intelligence)",
      "Predictive forecasting & lead scoring",
      "Lifecycle automation & advanced CRM",
      "Affiliate management & webinar funnels",
      "Team collaboration · API access · Custom branding",
    ],
    limits: ["3,000 AI conversations / month", "10,000 contacts", "Multiple funnels", "5 team members", "10 GB storage"],
  },
];

const AUDIENCES = [
  { title: "Creators", body: "Turn followers into customers with AI-powered engagement and monetization." },
  { title: "Coaches", body: "Automate lead nurturing, bookings, and client onboarding." },
  { title: "Consultants", body: "Capture opportunities, follow up faster, and close more deals." },
  { title: "Agencies", body: "Manage leads, automate workflows, and scale client acquisition." },
  { title: "SaaS Founders", body: "Build predictable growth systems powered by AI automation." },
  { title: "Digital Product Sellers", body: "Sell, market, and deliver products from one platform." },
];

const FAQS = [
  { q: "What is Ashra AI?", a: "Ashra AI is the AI Revenue Operating System for modern businesses. It combines AI agents, CRM, funnels, payments, and analytics in one platform so your business captures leads, closes customers, recovers lost revenue, and scales automatically — 24/7." },
  { q: "Do I need technical skills to use Ashra AI?", a: "No. Ashra AI is designed for creators, coaches, consultants, and operators — not engineers. Connect your business in minutes, activate your AI Revenue Team, and start seeing results without writing code." },
  { q: "How does the 15-day free trial work?", a: "Both IGNITE and ASCEND come with 15 days of free access. No charge today, full access during the trial. If you cancel before day 16, you won't be billed. If you don't cancel, your subscription begins on day 16." },
  { q: "Can I cancel anytime?", a: "Yes. Cancel from Settings → Billing or the Paddle customer portal. Cancellation takes effect at the end of your current paid cycle — you keep access until then." },
  { q: "Can I upgrade later?", a: "Absolutely. Start on IGNITE and upgrade to ASCEND the moment you need AI Strategist, predictive forecasting, team collaboration, or higher quotas. Upgrades take effect immediately." },
  { q: "Does it support WhatsApp?", a: "Yes. WhatsApp lead capture is included on both plans, so every inquiry from WhatsApp lands directly in your AI Inbox alongside Instagram and email." },
  { q: "Does it work for agencies?", a: "ASCEND is built for agencies: multiple workspaces, 5 team members, multi-brand support, lifecycle automation, affiliate systems, custom branding, and API access." },
];

// Sample blog posts for the homepage Growth Hub preview (shared with /blog page).
const FEATURED_POSTS = [
  { slug: "ai-revenue-team", category: "AI Growth", title: "Why every modern business needs an AI Revenue Team", excerpt: "The shift from tools to AI teammates is the single biggest leverage point for solo founders and agencies alike.", read: "8 min read", featured: true },
  { slug: "win-back-buyers", category: "Recovery", title: "The 3-message sequence that wins back 22% of cold buyers", excerpt: "A simple, plain-text follow-up that outperforms branded re-engagement campaigns.", read: "6 min read" },
  { slug: "funnel-fix", category: "Funnels", title: "Fixing the four leaks in your $0→$10K funnel", excerpt: "Where most creator funnels break — and the smallest changes that fix conversion.", read: "5 min read" },
];


export default function Landing() {
  return (
    <div className="min-h-screen bg-white text-zinc-900 antialiased" data-testid="landing-page">
      <TopNav />
      <Hero />
      <TrustStrip />
      <AITeam />
      <DashboardShowcase />
      <Features />
      <HowItWorks />
      <Pricing />
      <Audiences />
      <GrowthHubPreview />
      <FAQ />
      <FinalCTA />
      <SiteFooter />
      <style>{`
        .text-gradient { background-image: linear-gradient(90deg,#7C3AED 0%,#DB2777 50%,#D97706 100%); -webkit-background-clip: text; background-clip: text; color: transparent; }
        .bg-brand-gradient { background-image: linear-gradient(135deg,#6D28D9 0%,#DB2777 50%,#D97706 100%); }
        .ring-brand { box-shadow: 0 0 0 1px rgba(124,58,237,.18), 0 12px 40px -8px rgba(124,58,237,.25); }
        .glass-nav { background: rgba(255,255,255,.82); backdrop-filter: saturate(180%) blur(14px); }
      `}</style>
    </div>
  );
}


// ── 0. Nav ────────────────────────────────────────────────────────────────
function TopNav() {
  return (
    <header className="sticky top-0 z-40 border-b border-black/5 glass-nav">
      <div className="mx-auto max-w-7xl px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <img src={LOGO} alt="Ashra AI" className="h-10 sm:h-12 w-auto" />
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-sm text-zinc-700">
          <a href="#features" className="hover:text-zinc-900" data-testid="nav-features">Features</a>
          <a href="#pricing"  className="hover:text-zinc-900" data-testid="nav-pricing">Pricing</a>
          <Link to="/blog"     className="hover:text-zinc-900" data-testid="nav-growth-hub">Growth Hub</Link>
          <a href="#faq"      className="hover:text-zinc-900" data-testid="nav-faq">FAQ</a>
        </nav>
        <div className="flex items-center gap-2">
          <Link to="/login" className="hidden sm:inline-flex px-4 py-2 text-sm font-medium text-zinc-700 hover:text-zinc-900" data-testid="nav-login">Log in</Link>
          <Link to="/signup" className="inline-flex items-center gap-1 rounded-full bg-brand-gradient px-4 py-2 text-sm font-semibold text-white shadow-sm hover:shadow-md transition" data-testid="nav-cta">
            Start free trial <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </header>
  );
}


// ── 1. Hero ───────────────────────────────────────────────────────────────
function Hero() {
  return (
    <section className="relative overflow-hidden">
      {/* Soft glow background */}
      <div className="absolute inset-0 -z-10 pointer-events-none">
        <div className="absolute top-[-20%] left-1/2 -translate-x-1/2 h-[700px] w-[1100px] rounded-full bg-gradient-to-br from-violet-200/40 via-fuchsia-200/30 to-orange-200/30 blur-3xl" />
      </div>
      <div className="mx-auto max-w-7xl px-6 pt-24 sm:pt-28 pb-20 text-center">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-black/10 bg-white/70 backdrop-blur px-3 py-1 text-[11px] font-semibold tracking-wider text-zinc-700 uppercase">
            <Sparkles className="h-3 w-3 text-violet-600" /> THE AI REVENUE OPERATING SYSTEM
          </span>
        </motion.div>
        <motion.h1
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay: 0.05 }}
          className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter text-zinc-900"
        >
          Build Your <span className="text-gradient">AI Revenue Team</span>.
        </motion.h1>
        <motion.p
          initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay: 0.1 }}
          className="mt-6 mx-auto max-w-2xl text-lg sm:text-xl text-zinc-600 leading-relaxed"
        >
          AI agents that capture leads, close customers, recover lost revenue, and grow your business 24/7 — from one Revenue Operating System.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55, delay: 0.15 }}
          className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-3"
        >
          <Link to="/signup" data-testid="hero-primary-cta"
            className="inline-flex items-center gap-2 rounded-full bg-brand-gradient px-7 py-3.5 text-sm font-semibold text-white shadow-md hover:shadow-xl transition-all">
            Start your 15-day free trial <ArrowRight className="h-4 w-4" />
          </Link>
          <a href="#how" data-testid="hero-secondary-cta"
            className="inline-flex items-center gap-2 rounded-full border border-zinc-300 bg-white px-6 py-3.5 text-sm font-semibold text-zinc-800 hover:bg-zinc-50 transition">
            <PlayCircle className="h-4 w-4" /> See it work
          </a>
        </motion.div>
        <p className="mt-4 text-xs text-zinc-500">No charge today · Cancel anytime before billing</p>

        {/* Hero visual */}
        <motion.div
          initial={{ opacity: 0, y: 36 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.25 }}
          className="mt-16 mx-auto max-w-5xl"
        >
          <div className="rounded-2xl overflow-hidden ring-brand bg-white">
            <img src={HERO_VISUAL} alt="Ashra AI Revenue Operating System dashboard" className="w-full h-auto block" />
          </div>
        </motion.div>
      </div>
    </section>
  );
}


// ── 2. Trust strip ────────────────────────────────────────────────────────
function TrustStrip() {
  return (
    <section className="py-16 border-y border-black/5 bg-zinc-50/60">
      <div className="mx-auto max-w-7xl px-6">
        <p className="text-center text-[11px] font-semibold tracking-widest uppercase text-zinc-500 mb-8">
          Built for modern businesses
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5">
          {TRUST_AUDIENCES.map((a) => (
            <div key={a} className="flex items-center justify-center gap-2 text-sm font-medium text-zinc-700">
              <span className="inline-block h-2 w-2 rounded-full bg-gradient-to-br from-violet-500 to-orange-500" />
              {a}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


// ── 3. AI Team ────────────────────────────────────────────────────────────
function AITeam() {
  return (
    <section className="py-24 sm:py-28">
      <div className="mx-auto max-w-7xl px-6">
        <SectionHeading
          eyebrow="Meet your team"
          title={<>Four AI teammates. <span className="text-gradient">One unstoppable revenue engine</span>.</>}
          subtitle="They're not modules. They're colleagues who work together so you don't have to."
        />
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {AI_TEAM.map((m, i) => (
            <motion.div
              key={m.name}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="group relative rounded-2xl bg-white border border-zinc-200 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all"
              data-testid={`ai-team-card-${m.name.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className={`aspect-[5/4] w-full overflow-hidden bg-gradient-to-br ${m.accent}`}>
                <img src={m.img} alt={m.name} className="w-full h-full object-cover mix-blend-luminosity opacity-95 group-hover:scale-105 transition-transform duration-500" />
              </div>
              <div className="p-5">
                <p className="font-display text-lg font-bold text-zinc-900">{m.name}</p>
                <p className="text-sm text-zinc-600 mt-1">{m.role}</p>
                <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-zinc-100 px-2.5 py-1 text-[11px] font-medium text-zinc-700">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  {m.task}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}


// ── 4. Dashboard showcase ─────────────────────────────────────────────────
function DashboardShowcase() {
  return (
    <section className="py-24 sm:py-28 bg-zinc-50/70 border-y border-black/5">
      <div className="mx-auto max-w-7xl px-6">
        <SectionHeading
          eyebrow="One workspace"
          title={<>See your revenue scale, <span className="text-gradient">live</span>.</>}
          subtitle="Pipelines, conversations, AI activity, and growth metrics — every signal in one calm command center."
        />
        <motion.div
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.65 }}
          className="mt-14 mx-auto max-w-5xl"
        >
          <div className="rounded-2xl overflow-hidden ring-brand bg-white">
            <img src={DASH_MOCKUP} alt="Ashra AI Revenue Dashboard" className="w-full h-auto block" />
          </div>
        </motion.div>
      </div>
    </section>
  );
}


// ── 5. Features grid ──────────────────────────────────────────────────────
function Features() {
  return (
    <section id="features" className="py-24 sm:py-28">
      <div className="mx-auto max-w-7xl px-6">
        <SectionHeading
          eyebrow="Everything in one platform"
          title={<>The full revenue stack — <span className="text-gradient">no integrations required</span>.</>}
          subtitle="Eight powerful systems that work as one. Built for outcomes, not feature lists."
        />
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {FEATURES.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.4, delay: (i % 4) * 0.05 }}
              className="group rounded-2xl bg-white border border-zinc-200 p-5 hover:border-zinc-300 hover:shadow-md transition-all"
              data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-violet-50 to-orange-50 ring-1 ring-zinc-200 mb-4">
                <f.icon className="h-5 w-5 text-violet-600" />
              </div>
              <p className="font-semibold text-zinc-900">{f.title}</p>
              <p className="text-sm text-zinc-600 mt-1 leading-relaxed">{f.outcome}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}


// ── 6. How it works ───────────────────────────────────────────────────────
function HowItWorks() {
  const steps = [
    { n: 1, title: "Connect your business", body: "Hook up Instagram, WhatsApp, payments, and email in minutes." },
    { n: 2, title: "Activate your AI Revenue Team", body: "Hunter, Closer, Recovery, Strategist start working immediately." },
    { n: 3, title: "Scale revenue automatically", body: "Watch leads, conversions, and revenue grow — on autopilot." },
  ];
  return (
    <section id="how" className="py-24 sm:py-28 bg-zinc-50/70 border-y border-black/5">
      <div className="mx-auto max-w-7xl px-6">
        <SectionHeading
          eyebrow="How it works"
          title={<>From signup to scaling in <span className="text-gradient">three steps</span>.</>}
        />
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.45, delay: i * 0.08 }}
              className="relative rounded-2xl bg-white border border-zinc-200 p-8"
            >
              <div className="inline-flex items-center justify-center h-12 w-12 rounded-full bg-brand-gradient text-white font-display text-lg font-bold mb-5">{s.n}</div>
              <p className="font-display text-xl font-bold text-zinc-900">{s.title}</p>
              <p className="text-zinc-600 mt-2 leading-relaxed">{s.body}</p>
              {i < steps.length - 1 && (
                <ChevronRight className="hidden md:block absolute -right-4 top-1/2 -translate-y-1/2 h-6 w-6 text-zinc-300" />
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}


// ── 7. Pricing ────────────────────────────────────────────────────────────
function Pricing() {
  return (
    <section id="pricing" className="py-24 sm:py-28">
      <div className="mx-auto max-w-7xl px-6">
        <SectionHeading
          eyebrow="Pricing"
          title={<>Choose your <span className="text-gradient">revenue engine</span>.</>}
          subtitle="Two plans. Fifteen-day free trial on both. No charge today."
        />
        <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-5 max-w-5xl mx-auto">
          {PLANS.map((p) => (
            <div
              key={p.slug}
              className={`relative rounded-3xl border ${p.recommended ? "border-transparent ring-brand p-[1px] bg-brand-gradient" : "border-zinc-200"}`}
              data-testid={`pricing-card-${p.slug}`}
            >
              <div className={`rounded-3xl bg-white p-8 sm:p-10 h-full flex flex-col`}>
                <div className="flex items-center justify-between mb-4">
                  <p className="text-[11px] uppercase tracking-widest font-bold text-zinc-500">{p.name}</p>
                  {p.recommended && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-brand-gradient px-3 py-1 text-[10px] font-bold text-white uppercase tracking-wider">
                      <Star className="h-3 w-3" /> Recommended
                    </span>
                  )}
                </div>
                <div className="flex items-baseline gap-1.5">
                  <span className="font-display text-5xl font-bold text-zinc-900">${p.price}</span>
                  <span className="text-zinc-500 text-sm">/month</span>
                </div>
                <p className="text-sm text-zinc-600 mt-2">{p.audience}</p>

                <Link
                  to="/signup"
                  data-testid={`pricing-cta-${p.slug}`}
                  className={`mt-6 inline-flex items-center justify-center gap-1 rounded-full px-5 py-3 text-sm font-semibold transition ${
                    p.recommended ? "bg-brand-gradient text-white hover:shadow-lg" : "bg-zinc-900 text-white hover:bg-zinc-800"
                  }`}
                >
                  Start {p.name} <ArrowRight className="h-3.5 w-3.5" />
                </Link>

                <ul className="mt-6 space-y-2.5">
                  {p.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-sm text-zinc-700">
                      <Check className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" /> {b}
                    </li>
                  ))}
                </ul>

                <div className="mt-6 pt-6 border-t border-zinc-200">
                  <p className="text-[11px] uppercase tracking-wider font-semibold text-zinc-500 mb-2">Plan limits</p>
                  <ul className="space-y-1">
                    {p.limits.map((l) => (
                      <li key={l} className="text-xs text-zinc-600">· {l}</li>
                    ))}
                  </ul>
                </div>

                <div className="mt-6 pt-6 border-t border-zinc-200 grid grid-cols-1 gap-1.5 text-xs text-zinc-600">
                  <p className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-emerald-600" /> No charge today</p>
                  <p className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-emerald-600" /> Cancel anytime before billing</p>
                  <p className="flex items-center gap-2"><Check className="h-3.5 w-3.5 text-emerald-600" /> Full access during trial</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


// ── 8. Audience grid ──────────────────────────────────────────────────────
function Audiences() {
  return (
    <section className="py-24 sm:py-28 bg-zinc-50/70 border-y border-black/5">
      <div className="mx-auto max-w-7xl px-6">
        <SectionHeading
          eyebrow="Built for"
          title={<>Made for businesses that want to <span className="text-gradient">grow smarter</span>.</>}
          subtitle="Whether you're building an audience, selling services, managing clients, or scaling a digital business — Ashra AI gives you a Revenue Team that works around the clock."
        />
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {AUDIENCES.map((a, i) => (
            <motion.div
              key={a.title}
              initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.45, delay: i * 0.04 }}
              className="group rounded-2xl bg-white border border-zinc-200 p-6 hover:-translate-y-1 hover:shadow-md transition-all"
            >
              <p className="font-display text-lg font-bold text-zinc-900">{a.title}</p>
              <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{a.body}</p>
            </motion.div>
          ))}
        </div>
        <div className="text-center mt-12">
          <Link to="/signup" data-testid="audience-cta"
            className="inline-flex items-center gap-2 rounded-full bg-zinc-900 px-6 py-3 text-sm font-semibold text-white hover:bg-zinc-800 transition">
            Find your growth path <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </div>
    </section>
  );
}


// ── 9. Growth Hub preview ────────────────────────────────────────────────
function GrowthHubPreview() {
  const featured = FEATURED_POSTS[0];
  const others = FEATURED_POSTS.slice(1);
  return (
    <section className="py-24 sm:py-28">
      <div className="mx-auto max-w-7xl px-6">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 mb-12">
          <div>
            <p className="text-[11px] font-semibold tracking-widest uppercase text-zinc-500 mb-2">Growth Hub</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-zinc-900">
              Field notes for <span className="text-gradient">modern revenue teams</span>.
            </h2>
          </div>
          <Link to="/blog" data-testid="growth-hub-all" className="text-sm font-semibold text-zinc-700 hover:text-zinc-900 inline-flex items-center gap-1">
            All articles <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        <div className="grid lg:grid-cols-3 gap-5">
          {/* Featured */}
          <Link to={`/blog/${featured.slug}`} className="lg:col-span-2 group rounded-2xl bg-zinc-50 border border-zinc-200 overflow-hidden hover:shadow-md transition" data-testid="growth-hub-featured">
            <div className="aspect-[16/9] bg-brand-gradient relative">
              <div className="absolute inset-0 flex items-end p-8">
                <div className="text-white">
                  <span className="inline-block px-2.5 py-0.5 rounded-full bg-white/20 backdrop-blur text-[10px] uppercase tracking-wider font-semibold mb-3">{featured.category}</span>
                  <p className="font-display text-2xl sm:text-3xl font-bold tracking-tight max-w-xl">{featured.title}</p>
                </div>
              </div>
            </div>
            <div className="p-6">
              <p className="text-sm text-zinc-600">{featured.excerpt}</p>
              <p className="text-xs text-zinc-500 mt-3">{featured.read}</p>
            </div>
          </Link>
          {/* List */}
          <div className="space-y-4">
            {others.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="block rounded-2xl bg-white border border-zinc-200 p-5 hover:shadow-md transition" data-testid={`growth-hub-card-${p.slug}`}>
                <span className="inline-block px-2 py-0.5 rounded-full bg-zinc-100 text-[10px] uppercase tracking-wider font-semibold text-zinc-700">{p.category}</span>
                <p className="font-display text-lg font-bold text-zinc-900 mt-3">{p.title}</p>
                <p className="text-sm text-zinc-600 mt-1.5 leading-snug">{p.excerpt}</p>
                <p className="text-xs text-zinc-500 mt-3">{p.read}</p>
              </Link>
            ))}
            {/* Newsletter */}
            <div className="rounded-2xl bg-zinc-900 text-white p-5">
              <p className="font-display text-base font-bold">Get growth notes weekly</p>
              <p className="text-xs text-zinc-400 mt-1">One short, useful email every Friday.</p>
              <form className="mt-3 flex gap-2" onSubmit={(e) => { e.preventDefault(); }} data-testid="growth-hub-newsletter">
                <input
                  type="email" required placeholder="you@business.com"
                  className="flex-1 rounded-full bg-white/10 border border-white/10 px-3 py-2 text-xs placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
                <button type="submit" className="rounded-full bg-brand-gradient px-3 py-2 text-xs font-semibold">
                  <Mail className="h-3.5 w-3.5" />
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}


// ── 10. FAQ ───────────────────────────────────────────────────────────────
function FAQ() {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="py-24 sm:py-28 bg-zinc-50/70 border-y border-black/5">
      <div className="mx-auto max-w-3xl px-6">
        <SectionHeading
          eyebrow="FAQ"
          title={<>Questions, <span className="text-gradient">answered</span>.</>}
        />
        <div className="mt-12 space-y-3">
          {FAQS.map((f, i) => (
            <div key={i} className="rounded-2xl bg-white border border-zinc-200 overflow-hidden" data-testid={`faq-item-${i}`}>
              <button
                type="button"
                onClick={() => setOpen(open === i ? -1 : i)}
                className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-zinc-50/60 transition"
                aria-expanded={open === i}
              >
                <span className="font-semibold text-zinc-900 text-sm sm:text-base">{f.q}</span>
                {open === i ? <Minus className="h-4 w-4 text-zinc-500 flex-shrink-0" /> : <Plus className="h-4 w-4 text-zinc-500 flex-shrink-0" />}
              </button>
              {open === i && (
                <div className="px-5 pb-5 -mt-1 text-sm text-zinc-600 leading-relaxed">{f.a}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}


// ── 11. Final CTA ────────────────────────────────────────────────────────
function FinalCTA() {
  return (
    <section className="py-24 sm:py-32">
      <div className="mx-auto max-w-5xl px-6">
        <div className="rounded-3xl bg-brand-gradient text-white px-8 sm:px-16 py-16 sm:py-20 text-center shadow-xl">
          <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
            Ready to build your AI Revenue Team?
          </h2>
          <p className="mt-4 text-white/85 text-base sm:text-lg max-w-xl mx-auto">
            Start your 15-day free trial and put AI to work for your business.
          </p>
          <Link to="/signup" data-testid="final-cta"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-white text-zinc-900 px-7 py-3.5 text-sm font-semibold hover:bg-zinc-100 transition">
            Start free trial <ArrowRight className="h-4 w-4" />
          </Link>
          <p className="mt-3 text-xs text-white/75">No charge today.</p>
        </div>
      </div>
    </section>
  );
}


// ── shared section heading ───────────────────────────────────────────────
function SectionHeading({ eyebrow, title, subtitle }) {
  return (
    <div className="text-center max-w-3xl mx-auto">
      <p className="text-[11px] font-semibold tracking-widest uppercase text-zinc-500 mb-3">{eyebrow}</p>
      <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-zinc-900">{title}</h2>
      {subtitle && <p className="mt-4 text-base sm:text-lg text-zinc-600 leading-relaxed">{subtitle}</p>}
    </div>
  );
}
